import { datadogLogs } from '@datadog/browser-logs';
import queryString from 'query-string';
import { v4 as uuidv4 } from 'uuid';

import { store } from 'reduxStore';
import { MAX_RETRIES } from './constants';
import type { HTTPErrorResponse } from './error';
import { HTTPError } from './error';
import type { LOGGER_TYPE } from './types';
import { HttpStatusCode, LOG_ERROR_CATEGORY } from './types';

/** Cached auth token for synchronous access (e.g. beforeunload handlers). */
let cachedToken: string | null = null;

export function getLastToken(): string | null {
  return cachedToken;
}

interface SharedOptions {
  overwriteUrl?: string;
  additionalHeaders?: Record<string, string>;
  returnStatusCode?: boolean;
  prefix?: 'jupiter' | 'nebula' | 'sourcerer' | 'api';
  downloadingFile?: boolean;
  noAuth?: boolean;
  version?: string;
  returnRaw?: boolean;
  /**
   * Enable/disable automatic retry for this request
   * - undefined (default): Enabled for GET requests (5XX errors only)
   * - true: Explicitly enable retry
   * - false: Explicitly disable retry
   * Note: Only GET requests can be retried (mutations are not retried to avoid duplicate operations)
   */
  disableRetry?: boolean;
  /**
   * Maximum number of retries for 5XX errors (default: 3)
   */
  maxRetries?: number;
  /**
   * Custom correlation ID for this request. If not provided, one will be generated.
   * This ID persists across retry attempts for easier tracing.
   */
  correlationId?: string;
}

interface FetchOptions extends SharedOptions {
  resource: string;
  logger: LOGGER_TYPE;
  opts: any;
  formData?: boolean;
}

// Check if retries are enabled via environment variable
const isRetryEnabled = process.env.NEXT_PUBLIC_ENABLE_REQUEST_RETRY === 'true';

/**
 * Calculate exponential backoff delay
 * @param {number} attemptIndex - Zero-based attempt index (0 = first retry)
 * @return {number} Delay in milliseconds
 */
function calculateRetryDelay(attemptIndex: number): number {
  return Math.min(1000 * 2 ** attemptIndex, 30000); // 1s, 2s, 4s, max 30s
}

/**
 * Check if an error should be retried
 * @param {Error | HTTPError} error - The error (HTTPError for HTTP errors, Error for network errors)
 * @param {number} attemptCount - Current attempt count (0 = first attempt, 1 = first retry)
 * @param {number} maxRetries - Maximum number of retries
 * @param {boolean | undefined} disableRetry - Whether retry is explicitly disabled (undefined = enabled by default for GET)
 * @param {string} method - HTTP method (GET, POST, etc.)
 * @return {boolean} Whether the request should be retried
 */
function shouldRetry({
  error,
  attemptCount,
  maxRetries,
  disableRetry,
  method,
}: {
  error: Error | HTTPError;
  attemptCount: number;
  maxRetries: number;
  disableRetry: boolean | undefined;
  method: string;
}): boolean {
  // If retry is explicitly disabled, don't retry
  if (disableRetry === true) {
    return false;
  }

  // Don't retry if retries are disabled globally
  if (!isRetryEnabled) {
    return false;
  }

  // Don't retry if we've exceeded max retries
  if (attemptCount >= maxRetries) {
    return false;
  }

  // Only retry GET requests (safe/idempotent)
  // Mutations should not be retried to avoid duplicate operations
  if (method !== 'GET') {
    return false;
  }

  // Check if it's an HTTPError (has httpStatus property)
  if (error instanceof Error && 'httpStatus' in error) {
    const httpError = error as HTTPError;
    // Only retry on 5XX server errors
    if (httpError.httpStatus < 500 || httpError.httpStatus >= 600) {
      return false;
    }
    return true;
  }

  // For network errors (no httpStatus), retry if it's a GET request
  // Network errors include: connection failures, timeouts, network unavailable, etc.
  return true;
}

/**
 * Execute a single fetch attempt (without retry logic)
 */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
async function _fetchAttempt({
  resource,
  logger,
  opts,
  overwriteUrl = '',
  additionalHeaders = {},
  downloadingFile = false,
  formData = false,
  returnStatusCode = false,
  prefix = 'jupiter',
  version = 'v1',
  noAuth = false,
  returnRaw = false,
  correlationId,
}: FetchOptions & { correlationId: string }): Promise<any> {
  const ddLogger = datadogLogs.getLogger(logger);

  opts = {
    ...opts,
    credentials: 'same-origin', // pass cookies, for authentication
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Polyai-Correlation-Id': correlationId,
    },
  };

  if (formData) {
    delete opts.headers['Content-Type'];
  }

  opts['headers'] = { ...opts.headers, ...additionalHeaders };

  let token: string | undefined;
  if (!noAuth) {
    token = await store.getState().auth.getAuthToken();
    cachedToken = token ?? null;
    if (token) {
      opts.headers['Authorization'] = `Bearer ${token}`;
    } else if (ddLogger) {
      ddLogger.error('Auth token not found', {
        correlation_id: correlationId,
        resource,
        error_message: 'Auth token not found',
        error_category: LOG_ERROR_CATEGORY.SYSTEM,
        pathname: window.location.pathname,
      });
    }
  }
  const login = store.getState().auth.login;

  let url = overwriteUrl;
  if (!url) {
    if (prefix === 'sourcerer') {
      url = `${process.env.NEXT_PUBLIC_SOURCERER_API}/api/${version}/${resource}`;
    } else if (prefix === 'nebula') {
      url = `${process.env.NEXT_PUBLIC_NEBULA_API}/nebula/${version}/${resource}`;
    } else if (prefix === 'api') {
      url = `${process.env.NEXT_PUBLIC_API_SERVICE_URL}/${version}/${resource}`;
    } else {
      url = `${process.env.NEXT_PUBLIC_POLYAI_API}/jupiter/${version}/${resource}`;
    }
  }

  const res = await fetch(url, opts);

  if (res.ok) {
    const message = `Successful response from ${resource}`;
    if (!!ddLogger) {
      ddLogger.info(message, {
        correlation_id: correlationId,
        resource,
        success_message: message,
        status_code: res.status,
      });
    } else {
      console.log(`Missing DataDog Logger - ${logger}`);
    }

    if (downloadingFile) {
      const response = await res.blob();

      if (returnStatusCode) {
        return {
          data: response,
          headers: res.headers,
          statusCode: res.status as HttpStatusCode,
        };
      }

      return response;
    }

    let returnData;
    if (returnRaw) {
      returnData = await res.text();
    } else {
      returnData =
        res.status !== HttpStatusCode.SuccessNoContent ? await res.json() : {};
    }

    if (returnStatusCode) {
      return {
        data: returnData,
        statusCode: res.status as HttpStatusCode,
      };
    }

    return returnData;
  }
  if (res.status === HttpStatusCode.ClientErrorUnauthorized) {
    // Log 401 responses for debugging
    if (!!ddLogger) {
      ddLogger.warn('Received 401 Unauthorized response', {
        correlation_id: correlationId,
        resource,
        pathname: window.location.pathname,
        status_code: res.status,
      });
    }

    login(window.location.href);
    // Throw error to prevent undefined destructuring in calling code
    throw new HTTPError(
      'AuthUserUnauthorised',
      'Session expired. Redirecting to login...',
      res.status,
      'AuthUserUnauthorised',
      correlationId,
      {},
      correlationId,
    );
  }

  if (returnStatusCode) {
    let returnData;
    try {
      if (returnRaw) {
        returnData = await res.text();
      } else {
        returnData = await res.json();
      }
    } catch {
      returnData = returnRaw ? '' : {};
    }

    return {
      data: returnData,
      statusCode: res.status as HttpStatusCode,
    };
  }

  const {
    error_message: message,
    error_code: code,
    error_id: id,
    data,
  }: HTTPErrorResponse = await res.json();

  const error = new HTTPError(
    code,
    message,
    res.status,
    code,
    id,
    data,
    correlationId,
  );

  // log responses on DataDog
  if (!!ddLogger) {
    ddLogger.error(error.message, {
      correlation_id: correlationId,
      resource,
      error_message: error,
    });
  } else {
    console.log(`Missing DataDog Logger: ${logger}`);
  }
  throw error;
}

/**
 * Main fetch function with retry logic for direct API calls
 * Retries are handled in the request layer for both React Query and direct API calls
 */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
async function _fetch({
  resource,
  logger,
  opts,
  overwriteUrl = '',
  additionalHeaders = {},
  downloadingFile = false,
  formData = false,
  returnStatusCode = false,
  prefix = 'jupiter',
  version = 'v1',
  noAuth = false,
  returnRaw = false,
  disableRetry,
  maxRetries = MAX_RETRIES,
  correlationId: providedCorrelationId,
}: FetchOptions) {
  // Generate correlation ID once per logical request (persists across retries)
  const correlationId: string = providedCorrelationId || uuidv4();
  const ddLogger = datadogLogs.getLogger(logger);
  const method = opts.method || 'GET';

  let attemptCount = 0;
  let lastError: Error | HTTPError | null = null;

  while (attemptCount <= maxRetries) {
    try {
      const result = await _fetchAttempt({
        resource,
        logger,
        opts,
        overwriteUrl,
        additionalHeaders,
        downloadingFile,
        formData,
        returnStatusCode,
        prefix,
        version,
        noAuth,
        returnRaw,
        correlationId,
      });

      if (attemptCount !== 0 && !!ddLogger) {
        ddLogger?.info('Request succeeded after retries', {
          correlation_id: correlationId,
          resource,
          attempt_count: attemptCount,
          total_attempts: attemptCount + 1,
          max_retries: maxRetries,
          method,
        });
      }

      return result;
    } catch (error) {
      lastError = error as Error | HTTPError;
      const isHttpError =
        lastError instanceof Error && 'httpStatus' in lastError;
      const httpError = isHttpError ? (lastError as HTTPError) : null;

      // Check if we should retry
      if (
        !shouldRetry({
          error: lastError,
          attemptCount,
          maxRetries,
          disableRetry,
          method,
        })
      ) {
        // Log final failure if all retries exhausted
        if (!!ddLogger) {
          const logData: Record<string, any> = {
            correlation_id: correlationId,
            resource,
            attempt_count: attemptCount + 1,
            total_attempts: attemptCount + 1,
            max_retries: maxRetries,
            error_message: lastError.message,
            method,
            retry_disabled: disableRetry === true,
          };

          if (httpError) {
            logData.error_id = httpError.id;
            logData.error_code = httpError.code;
            logData.status_code = httpError.httpStatus;
          } else {
            logData.error_type = 'network_error';
          }

          const message =
            attemptCount >= maxRetries
              ? 'All retry attempts exhausted for request'
              : 'Request failed without retry';
          ddLogger.error(message, logData);
        }
        throw lastError;
      }

      // Calculate delay before retry
      const delay = calculateRetryDelay(attemptCount);
      attemptCount++;
      const retryAttempt = attemptCount; // 1-indexed retry attempt number
      const totalAttempts = attemptCount + 1; // Total attempts including initial

      // Log retry attempt
      if (!!ddLogger) {
        const logData: Record<string, any> = {
          correlation_id: correlationId,
          resource,
          retry_attempt: retryAttempt,
          total_attempts: totalAttempts,
          max_retries: maxRetries,
          error_message: lastError.message,
          retry_delay_ms: delay,
          method,
          retry_info: `Retry attempt ${retryAttempt} of ${maxRetries} (total attempt ${totalAttempts})`,
        };

        if (httpError) {
          logData.error_id = httpError.id;
          logData.error_code = httpError.code;
          logData.status_code = httpError.httpStatus;
          logData.error_type = 'http_5xx';
        } else {
          logData.error_type = 'network_error';
        }

        ddLogger.warn('Retrying request due to error', logData);
      }

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // This should never be reached, but TypeScript needs it
  throw lastError || new Error('Unexpected error in fetch retry loop');
}

interface GetOptions extends SharedOptions {
  params?: any;
  query?: string;
}

export async function doGet<T = any>(
  resource: string,
  logger: LOGGER_TYPE,
  { params, query = '', ...options }: GetOptions = {},
): Promise<T> {
  const opts = {
    method: 'GET',
  };
  const queryString = generateQueryString(params, query);
  return _fetch({
    ...options,
    resource: resource + queryString,
    logger,
    opts,
  });
}

function generateQueryString(params: any, query: string) {
  const qs = params
    ? queryString.stringify(params, {
        skipNull: true,
        skipEmptyString: true,
      })
    : '';

  const cleanedQueryString = qs ? `?${qs}` : '';

  let _query = cleanedQueryString ? `&${query}` : `?${query}`;
  if (_query.length === 1) {
    _query = '';
  }

  return cleanedQueryString + _query;
}

interface PatchOptions extends SharedOptions {
  postingFile?: boolean;
}

export async function doPatch<T = any>(
  resource: string,
  logger: LOGGER_TYPE,
  body: any = {},
  { postingFile = false, ...options }: PatchOptions = {},
) {
  const opts = {
    method: 'PATCH',
    body: postingFile ? body : JSON.stringify(body),
  };

  return _fetch({
    ...options,
    resource,
    logger,
    opts,
    formData: postingFile,
  }) as Promise<T>;
}

interface PutOptions extends SharedOptions {
  postingFile?: boolean;
  parseResponseToJSON?: boolean;
  formData?: boolean;
}

export async function doPut<T = any>(
  resource: string,
  logger: LOGGER_TYPE,
  body: any = {},
  {
    postingFile = false,
    parseResponseToJSON,
    downloadingFile = false,
    ...options
  }: PutOptions = {},
) {
  const opts = {
    method: 'PUT',
    body: postingFile ? body : JSON.stringify(body),
  };

  return _fetch({
    ...options,
    resource,
    logger,
    opts,
    downloadingFile: (postingFile && !parseResponseToJSON) || downloadingFile,
    formData: postingFile,
  }) as Promise<T>;
}

interface PostOptions extends SharedOptions {
  postingFile?: boolean;
  /* Posting a file would result in a blob response, use this to make it JSON format */
  parseResponseToJSON?: boolean;
}

export async function doPost<T = any>(
  resource: string,
  logger: LOGGER_TYPE,
  body: any = {},
  {
    postingFile = false,
    downloadingFile = false,
    parseResponseToJSON,
    ...options
  }: PostOptions = {},
) {
  const opts = {
    method: 'POST',
    body: postingFile ? body : JSON.stringify(body),
  };

  return _fetch({
    ...options,
    resource,
    logger,
    opts,
    downloadingFile: (postingFile && !parseResponseToJSON) || downloadingFile,
    formData: postingFile,
  }) as Promise<T>;
}

export async function doDelete<T = any>(
  resource: string,
  logger: LOGGER_TYPE,
  body: any = {},
  options: SharedOptions = {},
) {
  const opts = {
    method: 'DELETE',
    body: JSON.stringify(body),
  };

  return _fetch({
    ...options,
    resource,
    logger,
    opts,
  }) as Promise<T>;
}

// Helper function to build API URL based on prefix and version
export function buildApiUrl(
  resource: string,
  prefix: 'jupiter' | 'nebula' | 'sourcerer' | 'api' = 'jupiter',
  version: string = 'v1',
): string {
  if (prefix === 'sourcerer') {
    return `${process.env.NEXT_PUBLIC_SOURCERER_API}/api/${version}/${resource}`;
  }
  if (prefix === 'nebula') {
    return `${process.env.NEXT_PUBLIC_NEBULA_API}/nebula/${version}/${resource}`;
  }
  if (prefix === 'api') {
    return `${process.env.NEXT_PUBLIC_API_SERVICE_URL}/${version}/${resource}`;
  }
  return `${process.env.NEXT_PUBLIC_POLYAI_API}/jupiter/${version}/${resource}`;
}

// Helper function to build API headers with auth token and correlation ID
export async function buildApiHeaders(
  additionalHeaders: Record<string, string> = {},
  noAuth: boolean = false,
): Promise<Record<string, string>> {
  const correlationID = uuidv4();
  const headers: Record<string, string> = {
    'X-Polyai-Correlation-Id': correlationID,
    ...additionalHeaders,
  };

  const token = await store.getState().auth.getAuthToken();
  if (token && !noAuth) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

// Helper function to process Server-Sent Events stream
export async function processSSEStream(
  response: Response,
  onMessage: (data: string) => void,
): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Response body is not readable');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  const timeoutMs = 300000; // 5 minutes
  const startTime = Date.now();

  try {
    while (true) {
      if (Date.now() - startTime > timeoutMs) {
        throw new Error('Stream timeout exceeded');
      }

      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          onMessage(line.slice(6));
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
